import React, { ChangeEvent, MouseEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './RecordFiles.module.scss';

let nextId = 0;

export interface RecordFileUploadProps {
    uploading: boolean;
    onFiles: (files: File[]) => void;
}

export const RecordFileUpload = ({ uploading, onFiles }: RecordFileUploadProps) => {
    const { t } = useTranslation();
    const idRef = useRef<string>();
    if (!idRef.current) idRef.current = `record-files-upload-${++nextId}`;
    const inputId = idRef.current;
    const hintId = `${inputId}-hint`;

    // aria-disabled (not disabled) keeps the input focusable during an upload; block the picker instead.
    const handleClick = (event: MouseEvent<HTMLInputElement>) => {
        if (uploading) event.preventDefault();
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files ? Array.from(event.target.files) : [];
        // Clear so choosing the same file again still fires a change.
        event.target.value = '';
        if (uploading) return;
        if (files.length) onFiles(files);
    };

    return (
        <div className={styles.upload}>
            <label htmlFor={inputId} className={styles.uploadLabel}>
                {t('RecordFiles.UploadLabel', 'Upload files')}
            </label>
            <input
                id={inputId}
                type="file"
                multiple
                className={uploading ? styles.uploadBusy : undefined}
                aria-disabled={uploading}
                aria-describedby={hintId}
                onClick={handleClick}
                onChange={handleChange}
            />
            <span id={hintId} className={styles.hint}>
                {t('RecordFiles.UploadHint', 'Maximum 50 MB per file.')}
            </span>
        </div>
    );
};
